import { Component, type ErrorInfo, type ReactNode } from "react"
import ServerErrorPage from "@/pages/errors/ServerError"
import { captureError } from "@/lib/sentry"
import { isStaleChunkError, reloadOnceForStaleChunk } from "@/lib/stale-chunk"

interface State {
  error: Error | null
}

export class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", error, info.componentStack)
    // Report unhandled render errors to Sentry (no-op if DSN unset).
    captureError(error, { componentStack: info.componentStack })
    // A page file renamed by a deploy: one reload fetches the new names. If the
    // guard refuses (already reloaded, or no sessionStorage) the error page
    // stays, and its buttons reload.
    if (isStaleChunkError(error)) reloadOnceForStaleChunk()
  }

  render() {
    if (this.state.error) {
      return <ServerErrorPage error={this.state.error} />
    }
    return this.props.children
  }
}
